<template>
    <v-card class="d-inline-block mr-4"
            @dragover.prevent="handleDragOver"
            @drop.prevent="handleFileDrop"
            min-width="100%">
        <v-toolbar v-if="repeatableChild">
            <v-icon class="drag-handle" :title="$t('common.actions.move')" size="x-large">
                mdi-drag-vertical
            </v-icon>
            <v-card-title>{{ cardTitle }}</v-card-title>
        </v-toolbar>

        <template v-else>
            <v-card-title>{{ cardTitle }}</v-card-title>
            <v-card-subtitle v-if="props.hint">{{ props.hint }}</v-card-subtitle>
        </template>

        <input type="file" ref="uploader" style="display: none" @change="handleFileSelect">

        <v-img v-if="type === 'image'"
               :src="imageSrc"
               :lazy-src="`${base_url}/resources/redcode/img/favicon/apple-touch-icon.png`"
               @click="handleImageClick"
               v-bind="props.vuetify_config"
               max-height="300"
               cover
               :aspect-ratio="1"
               class="white--text align-end mx-auto">
            <div v-if="is_uploading" class="d-flex align-center justify-center fill-height">
                <v-progress-circular :rotate="0"
                                     :size="50"
                                     :width="4"
                                     :model-value="upload_progress"
                                     color="primary">
                    {{ upload_progress || 0 }}%
                </v-progress-circular>
            </div>
        </v-img>

        <div v-else id="iframe-wrapper">
            <iframe v-if="iFrameUrl" :src="iFrameUrl" frameborder="0"></iframe>

            <div v-else class="d-flex align-center justify-center text-medium-emphasis" style="min-height: 150px;">
                {{ $t('editor.fields.media.no_id', {type: cardTitle}) }}
            </div>

            <v-text-field v-if="show_id_field"
                          v-model="url"
                          :label="inputLabel"
                          variant="solo"
                          @keyup.enter="show_id_field = false">
                <template v-if="url" v-slot:append-inner>
                    <v-icon @click="show_id_field = false">mdi-check</v-icon>
                </template>
            </v-text-field>
        </div>

        <v-card-actions>
            <v-btn v-if="type !== 'image'" @click="show_id_field = !show_id_field">
                <v-icon v-if="type === 'youtube'" :title="$t('editor.fields.media.edit_youtube_id')">mdi-youtube</v-icon>
                <v-icon v-if="type === 'vimeo'" :title="$t('editor.fields.media.edit_vimeo_id')">mdi-vimeo</v-icon>
            </v-btn>

            <v-spacer></v-spacer>

            <v-menu>
                <template v-slot:activator="{ props: menuProps }">
                    <v-btn icon="mdi-dots-vertical"
                           size="x-small"
                           variant="flat"
                           class="ml-auto"
                           v-bind="menuProps">
                    </v-btn>
                </template>

                <v-list density="compact">
                    <v-list-item @click="toggleFileSelect">
                        <v-list-item-title :title="$t('editor.fields.upload.max_size', {size: humanFileSize(max_upload_size, true)})">
                            <v-icon>mdi-tray-arrow-up</v-icon> {{ $t('editor.fields.media.upload_image') }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="toggleType('youtube')">
                        <v-list-item-title :title="$t('editor.fields.media.embed_youtube')">
                            <v-icon>mdi-youtube</v-icon> YouTube <!-- i18n-ignore: Markenname -->
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="toggleType('vimeo')">
                        <v-list-item-title :title="$t('editor.fields.media.embed_vimeo')">
                            <v-icon>mdi-vimeo</v-icon> Vimeo <!-- i18n-ignore: Markenname -->
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="isDeletable" @click="handleDeleteCustomised">
                        <v-list-item-title :title="$t('editor.fields.media.delete_uploaded')">
                            {{ $t('common.actions.delete') }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-card-actions>

        <ul v-if="warnings.length">
            <li v-for="(warning, index) in warnings" :key="index">
                {{ warning }}
                <v-btn @click="warnings.splice(index, 1)" icon="mdi-close"></v-btn>
            </li>
        </ul>
    </v-card>
</template>

<script setup>
import {computed, ref} from "vue";
import {humanFileSize} from "../../utils/helpers";
import * as api from "../../services/api";
import jp from "jsonpath";
import {mandatory_props, resolveFieldPath} from "../../utils/editorConfigFieldHelpers";
import {isStepDraftStore} from "../../utils/editorFieldPersistence.mjs";
import {getStoreByName} from "../../utils/storeHelpers";
import {useCampaignStore} from "../../Stores/CampaignStore";
import {useI18n} from "@/i18n";

const {t} = useI18n();

defineExpose({toggleFileSelect});

const max_upload_size = 500000; // 500 KB
const warnings = ref([]);
const is_uploading = ref(false);
const uploader = ref(null);
const upload_progress = ref(0);
const show_id_field = ref(false);

const base_url = import.meta.env.VITE_DELIVERY_SYSTEM_URL;

const props = defineProps({
    ...mandatory_props,
    repeatableChild: {
        type: Boolean,
        default: false,
    }
});

const store = getStoreByName(props.storeName);
const campaignStore = useCampaignStore();

/**
 * The effective json_path of this field.
 * @see resolveFieldPath() - handles fields inside (repeatable) SubPanels
 */
const jsonPath = computed(() => resolveFieldPath(store, props.json_path));

const url = computed({
    get() {
        if (!jsonPath.value) {
            return null;
        }

        return jp.value(store[props.jsonKey], `${jsonPath.value}.url`);
    },
    set(v) {
        if (!jsonPath.value) {
            console.error('Could not set media url: jsonPath is missing.', { // i18n-ignore (Konsole)
                props,
                current_window_props: store.current_window_props,
            });
            return;
        }

        jp.value(store[props.jsonKey], `${jsonPath.value}.url`, v);
    }
});

const type = computed({
    get() {
        if (!jsonPath.value) {
            return 'image';
        }

        return jp.value(store[props.jsonKey], `${jsonPath.value}.type`) || 'image';
    },
    set(v) {
        if (!jsonPath.value) {
            console.error('Could not set media type: jsonPath is missing.', { // i18n-ignore (Konsole)
                props,
                current_window_props: store.current_window_props,
            });
            return;
        }

        jp.value(store[props.jsonKey], `${jsonPath.value}.type`, v);
    }
});

const imageSrc = computed(() => {
    if (typeof url.value !== 'string' || url.value.trim() === '') {
        return `${base_url}/resources/redcode/img/favicon/apple-touch-icon.png`;
    }

    if (url.value.startsWith('http')) {
        return url.value;
    }

    return `${base_url}${url.value}`;
});

const cardTitle = computed(() => {
    if (type.value === 'youtube') return t('editor.fields.media.types.youtube');
    if (type.value === 'vimeo') return t('editor.fields.media.types.vimeo');

    return t('editor.fields.media.types.image');
});

const inputLabel = computed(() => {
    return type.value === 'youtube'
        ? t('editor.fields.media.youtube_id')
        : t('editor.fields.media.vimeo_id');
});

const iFrameUrl = computed(() => {
    if (!url.value) {
        return null;
    }

    if (type.value === 'youtube') {
        return `https://www.youtube-nocookie.com/embed/${url.value}?rel=0&amp;showinfo=0`;
    }

    if (type.value === 'vimeo') {
        return `https://player.vimeo.com/video/${url.value}?title=0&byline=0&portrait=0&badge=0`;
    }

    return null;
});

const emits = defineEmits(['deleted']);

// ----------------------------------------------------------------------------
// Store / Revision Helpers
// ----------------------------------------------------------------------------
function getCampaignFieldFromJsonPath() {
    if (!jsonPath.value) {
        return null;
    }

    const parsed = jp.parse(jsonPath.value);

    return parsed[2]?.expression?.value || null;
}

function getComponentEditorBaseRevision(field) {
    if (props.storeName !== 'ComponentEditStore') {
        return null;
    }

    if (store.editingRevisionField !== field) {
        return null;
    }

    return store.baseRevision;
}

function syncComponentEditorRevision(responseData, field) {
    if (props.storeName !== 'ComponentEditStore') {
        return;
    }

    if (typeof store.syncBaseRevisionFromCampaignResponse === 'function') {
        store.syncBaseRevisionFromCampaignResponse(responseData, field);
    }
}

function persistMediaChange() {
    if (isStepDraftStore(props.storeName)) {
        return Promise.resolve();
    }

    const field = getCampaignFieldFromJsonPath();

    if (field === 'components') {
        return campaignStore
            .updateComponents(getComponentEditorBaseRevision('components'))
            .then(response => {
                syncComponentEditorRevision(response, 'components');

                return response;
            });
    }

    if (field === 'meta') {
        return campaignStore
            .updateMeta(getComponentEditorBaseRevision('meta'))
            .then(response => {
                syncComponentEditorRevision(response, 'meta');

                return response;
            });
    }

    if (typeof store.save === 'function') {
        return store.save();
    }

    return Promise.resolve();
}

function handleMediaSaveError(error, previousState = null) {
    if (previousState) {
        type.value = previousState.type;
        url.value = previousState.url;
    }

    if (campaignStore.handleExpiredEditorSession(error)) {
        return;
    }

    if (campaignStore.handleStaleRevision(error)) {
        return;
    }

    console.error('Media field save failed:', error); // i18n-ignore (Konsole)
}

function getAssetUploadUrl() {
    if (typeof store.getAssetUploadUrl === 'function') {
        return store.getAssetUploadUrl();
    }

    return window.route('assets.storeForStep', {step: store.uuid});
}

function getAssetDeleteUrl() {
    if (props.storeName === 'ComponentEditStore') {
        return store.getAssetDeleteUrl({path: url.value});
    }

    if (typeof store.getAssetDeleteUrl === 'function') {
        return store.getAssetDeleteUrl(jsonPath.value);
    }

    return window.route('assets.deleteCustomised', {
        step: store.uuid,
        json_path: jsonPath.value,
    });
}

function confirmMediaDelete() {
    const message = props.label
        ? t('editor.fields.media.confirm_delete_named', {label: props.label})
        : t('editor.fields.media.confirm_delete');

    return window.confirm(message);
}

// ----------------------------------------------------------------------------
// Show Assets
// ----------------------------------------------------------------------------
function toggleType(new_type) {
    type.value = new_type;
    url.value = null;

    if (new_type !== 'image') {
        show_id_field.value = true;
    }
}

function handleImageClick() {
    if (!url.value) {
        return toggleFileSelect();
    }

    handleShowCurrent();
}

function handleShowCurrent() {
    if (!url.value) {
        return;
    }

    const w = window.open(imageSrc.value, '_blank');
    w.focus();
}

function handleShowOriginal() {
    const originalUrl = window.route('assets.getOriginal', {
        step: store.uuid,
        json_path: jsonPath.value
    });

    api.client.get(originalUrl).then(response => {
        const w = window.open(`${base_url}${response.data.original_path}`, '_blank');
        w.focus();
    });
}

// ----------------------------------------------------------------------------
// File Upload
// ----------------------------------------------------------------------------
function toggleFileSelect() {
    uploader.value.click();
}

// Do not delete this function, it is needed for the drag and drop function!
function handleDragOver(event) {
}

function handleFileDrop(event) {
    event.preventDefault();

    let source = event.dataTransfer.items || event.dataTransfer.files;

    for (let i = 0; i < source.length; i++) {
        if (source[i].kind === 'file') {
            let file = source[i].getAsFile();

            if (file.size < max_upload_size) {
                uploadFileForStep(file);
            } else {
                addWarning(file);
            }
        }
    }
}

function handleFileSelect(event) {
    for (let i = 0; i < event.target.files.length; i++) {
        let file = event.target.files[i];

        if (file.size < max_upload_size) {
            uploadFileForStep(file);
        } else {
            addWarning(file);
        }
    }

    event.target.value = '';
}

function uploadFileForStep(file) {
    const postUrl = getAssetUploadUrl();
    const form = new FormData();
    form.append('asset', file);

    const previousState = {
        type: type.value,
        url: url.value,
    };

    let config = {
        onUploadProgress: (progressEvent) => {
            upload_progress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
    };

    is_uploading.value = true;

    api.client.post(postUrl, form, config).then(response => {
        type.value = 'image';
        url.value = response.data.path;

        return persistMediaChange();
    }).catch(error => {
        handleMediaSaveError(error, previousState);
    }).finally(() => {
        is_uploading.value = false;
    });
}

function addWarning(file) {
    warnings.value.push(t('editor.fields.upload.too_large', {
        name: file.name,
        size: humanFileSize(file.size, true),
        max: humanFileSize(max_upload_size, true),
    }));
}

// ----------------------------------------------------------------------------
// Delete / Restore
// ----------------------------------------------------------------------------
const isDeletable = computed(() => {
    return type.value === 'image' && Boolean(url.value);
});

function handleDeleteCustomised() {
    if (!url.value) {
        emits('deleted', jsonPath.value);
        return;
    }

    if (!confirmMediaDelete()) {
        return;
    }

    const previousState = {
        type: type.value,
        url: url.value,
    };

    const actionUrl = getAssetDeleteUrl();

    if (props.storeName === 'ComponentEditStore') {
        type.value = 'image';
        url.value = "";

        persistMediaChange()
            .then(() => api.client.delete(actionUrl))
            .then(() => {
                if (props.repeatableChild) {
                    emits('deleted', jsonPath.value);
                }
            })
            .catch(error => {
                handleMediaSaveError(error, previousState);
            });

        return;
    }

    api.client.delete(actionUrl).then(response => {
        if (response.data.original_path) {
            type.value = 'image';
            url.value = response.data.original_path;

            return persistMediaChange();
        }

        emits('deleted', jsonPath.value);
    }).catch(error => {
        handleMediaSaveError(error, previousState);
    });
}
</script>

<style lang="scss" scoped>
#iframe-wrapper {
    position: relative;

    iframe {
        width: 100%;
        height: 100%;
        min-height: 150px;
        aspect-ratio: 16/9;
        z-index: 0;
        position: relative;
    }

    .v-text-field {
        position: absolute;
        z-index: 100;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        min-width: 50%;
    }
}
</style>
